import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getMessaging, getToken, onMessage, isSupported as isMessagingSupported, MessagePayload } from 'firebase/messaging';

// Firebase configuration
// Get these values from Firebase Console > Project Settings > General > Your apps
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyBzwG794hV-UjfaZnMvAyqlyM-FjhTttts",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "connectandgo-f556f.firebaseapp.com",
  // Realtime Database URL - required for chat functionality
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || "https://connectandgo-f556f-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "connectandgo-f556f",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "connectandgo-f556f.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "505896990567",
  // Use treffpunkts Web app by default (if env vars are missing)
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:505896990567:web:7bbcb24851a93bbeed21c2",
  measurementId: import.meta.env.VITE_GOOGLE_ANALYTICS_ID || "G-Q5NBWXYVR1"
};

// Debug Firebase configuration
console.log('🔥 Firebase Configuration Debug:', {
  hasApiKey: !!firebaseConfig.apiKey && firebaseConfig.apiKey !== "your-api-key",
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasDatabaseURL: !!firebaseConfig.databaseURL,
  hasProjectId: !!firebaseConfig.projectId,
  hasStorageBucket: !!firebaseConfig.storageBucket,
  hasMessagingSenderId: !!firebaseConfig.messagingSenderId && firebaseConfig.messagingSenderId !== "your-sender-id",
  hasAppId: !!firebaseConfig.appId && firebaseConfig.appId !== "your-app-id",
  hasMeasurementId: !!firebaseConfig.measurementId && firebaseConfig.measurementId !== "your-measurement-id",
  usingEnvVars: {
    apiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
    databaseURL: !!import.meta.env.VITE_FIREBASE_DATABASE_URL,
    messagingSenderId: !!import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: !!import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: !!import.meta.env.VITE_GOOGLE_ANALYTICS_ID
  },
  config: {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    databaseURL: firebaseConfig.databaseURL,
    messagingSenderId: firebaseConfig.messagingSenderId,
    // Don't log sensitive keys in production
    apiKeyPresent: firebaseConfig.apiKey?.length > 10,
    appIdPresent: firebaseConfig.appId?.length > 10
  }
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);
// Use the device/browser language for Firebase Auth UI and flows
if (typeof window !== 'undefined') {
  try {
    // In modular SDK, this method exists on the Auth instance
    // and helps avoid language mismatches in Google sign-in prompts
    (auth as unknown as { useDeviceLanguage: () => void }).useDeviceLanguage?.();
  } catch {
    // no-op if not supported
  }
}

// Initialize Firebase Storage and get a reference to the service
export const storage = getStorage(app);

// Initialize Firebase Analytics (only in browser environment)
let analytics: ReturnType<typeof getAnalytics> | null = null;
if (typeof window !== 'undefined') {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

// Initialize Firebase Messaging (only in browser environment)
let messaging: ReturnType<typeof getMessaging> | null = null;
if (typeof window !== 'undefined') {
  isMessagingSupported().then((supported) => {
    if (supported) {
      messaging = getMessaging(app);
    }
  });
}

// FCM Vapor Key (Web Push certificate key from Firebase Console)
export const vapidKey = "BHBL3R0raYr8NfJU9loP6te8BRJvHaO8V6Porv2fzmAVHTwdIUS_J-oiEJJMCroHQOwysY8h6pW6QYpHE60JfB0";

// FCM Token management with enhanced debugging
export const getFCMToken = async (): Promise<string | null> => {
  console.log('🔄 Attempting to get FCM token...');
  
  if (!messaging) {
    console.error('❌ Firebase Messaging not initialized');
    console.log('🔍 Messaging initialization status:', {
      messagingExists: !!messaging,
      windowExists: typeof window !== 'undefined',
      serviceWorkerSupported: 'serviceWorker' in navigator,
      notificationSupported: 'Notification' in window
    });
    return null;
  }

  try {
    console.log('🔑 Using VAPID Key:', vapidKey);
    console.log('🔍 Requesting notification permission...');
    
    // Check notification permission
    const permission = await Notification.requestPermission();
    console.log('🔔 Notification permission:', permission);
    
    if (permission !== 'granted') {
      console.warn('⚠️ Notification permission not granted');
      return null;
    }

    console.log('🔄 Generating FCM token with messaging service...');
    const token = await getToken(messaging, { vapidKey });
    
    if (token) {
      console.log('✅ FCM Token generated successfully');
      console.log('🔍 Token length:', token.length);
      console.log('🔍 Token preview:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.error('❌ FCM token is null or empty');
      return null;
    }
  } catch (error) {
    console.error('❌ Error generating FCM token:', error);
    console.log('🔍 Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : 'No stack trace'
    });
    
    // Common FCM token errors and solutions
    if (error instanceof Error) {
      if (error.message.includes('messaging/unsupported-browser')) {
        console.log('💡 Solution: Browser doesn\'t support push messaging');
      } else if (error.message.includes('messaging/permission-blocked')) {
        console.log('💡 Solution: Enable notifications in browser settings');
      } else if (error.message.includes('messaging/vapid-key-required')) {
        console.log('💡 Solution: VAPID key is missing or invalid');
      } else if (error.message.includes('messaging/registration-token-not-registered')) {
        console.log('💡 Solution: Service worker registration failed');
      }
    }
    
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback: (payload: MessagePayload) => void) => {
  if (!messaging) {
    console.warn('Firebase Messaging not supported');
    return () => {};
  }

  return onMessage(messaging, callback);
};

export { analytics, messaging };
export default app; 